const MUTE_KEY = "coop-detat/muted";

let ac: AudioContext | null = null;
let master: GainNode | null = null;
let noiseBuf: AudioBuffer | null = null;
let muted = localStorage.getItem(MUTE_KEY) === "1";

function audio(): { ac: AudioContext; out: GainNode; noise: AudioBuffer } | null {
  if (muted) return null;
  if (!ac) {
    if (typeof AudioContext === "undefined") return null;
    ac = new AudioContext();
    master = ac.createGain();
    master.gain.value = 0.5;
    master.connect(ac.destination);
    noiseBuf = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
    const d = noiseBuf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  if (ac.state === "suspended") void ac.resume();
  if (!master || !noiseBuf) return null;
  return { ac, out: master, noise: noiseBuf };
}

function tone(freq: number, dur: number, type: OscillatorType, vol: number, slideTo?: number, delay = 0): void {
  const a = audio();
  if (!a) return;
  const t = a.ac.currentTime + delay;
  const o = a.ac.createOscillator();
  const g = a.ac.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(a.out);
  o.start(t);
  o.stop(t + dur + 0.05);
}

function noise(dur: number, vol: number, freq: number, q: number, slideTo?: number, delay = 0, type: BiquadFilterType = "bandpass"): void {
  const a = audio();
  if (!a) return;
  const t = a.ac.currentTime + delay;
  const src = a.ac.createBufferSource();
  src.buffer = a.noise;
  src.loop = true;
  const f = a.ac.createBiquadFilter();
  f.type = type;
  f.frequency.setValueAtTime(freq, t);
  if (slideTo) f.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
  f.Q.value = q;
  const g = a.ac.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + Math.min(0.05, dur / 4));
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(f).connect(g).connect(a.out);
  src.start(t, Math.random() * 0.5);
  src.stop(t + dur + 0.05);
}

export const sfx = {
  click: () => tone(660, 0.06, "square", 0.08, 880),
  step: () => noise(0.08, 0.15, 400, 1, 200),
  swoosh: () => noise(0.22, 0.25, 600, 2, 3000),
  hit: () => {
    noise(0.18, 0.6, 900, 0.8, 150);
    tone(140, 0.18, "sine", 0.5, 50);
  },
  crit: () => {
    noise(0.3, 0.7, 1200, 0.7, 100);
    tone(220, 0.3, "square", 0.25, 40);
  },
  block: () => {
    tone(1400, 0.25, "triangle", 0.25, 1100);
    tone(2300, 0.15, "sine", 0.12);
    noise(0.08, 0.3, 3000, 2);
  },
  cluck: () => {
    tone(700, 0.07, "square", 0.12, 420);
    tone(820, 0.09, "square", 0.12, 480, 0.1);
  },
  taunt: () => {
    for (let i = 0; i < 4; i++) tone(600 + i * 60, 0.07, "square", 0.1, 380, i * 0.12);
  },
  roar: () => {
    tone(330, 0.15, "sawtooth", 0.2, 440);
    tone(440, 0.15, "sawtooth", 0.2, 520, 0.15);
    tone(520, 0.6, "sawtooth", 0.22, 300, 0.3);
    noise(0.8, 0.2, 800, 1, 300, 0.1);
  },
  throw: () => noise(0.25, 0.2, 500, 3, 2000),
  splat: () => {
    noise(0.25, 0.6, 600, 1, 120);
    tone(300, 0.15, "sine", 0.3, 80);
  },
  rest: () => tone(392, 0.5, "sine", 0.1, 330),
  heal: () => [523, 659, 784, 1046].forEach((f, i) => tone(f, 0.25, "triangle", 0.12, undefined, i * 0.08)),
  coin: () => {
    tone(988, 0.08, "square", 0.08);
    tone(1319, 0.25, "square", 0.08, undefined, 0.08);
  },
  levelup: () => [523, 659, 784, 1046, 1319].forEach((f, i) => tone(f, 0.3, "square", 0.08, undefined, i * 0.1)),
  cheer: (vol = 0.3) => {
    noise(1.4, vol, 1200, 0.6, 900);
    noise(1.2, vol * 0.6, 2500, 1, 1800, 0.1);
  },
  boo: () => noise(0.9, 0.25, 300, 2, 200),
  ko: () => {
    tone(440, 0.8, "sawtooth", 0.15, 60);
    noise(0.5, 0.4, 500, 1, 100);
  },
  fanfare: () => [392, 523, 659, 784, 659, 784].forEach((f, i) => tone(f, i === 5 ? 0.6 : 0.16, "square", 0.09, undefined, [0, 0.15, 0.3, 0.45, 0.65, 0.8][i])),
  sad: () => [392, 370, 349, 330].forEach((f, i) => tone(f, 0.4, "triangle", 0.12, undefined, i * 0.35)),
};

export function isMuted(): boolean {
  return muted;
}

export function setMuted(m: boolean): void {
  muted = m;
  localStorage.setItem(MUTE_KEY, m ? "1" : "0");
  if (m) void ac?.suspend();
}
